import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertCircle, Download, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { TopTabs } from '@/components/dashboard/TopTabs'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ApprovalModal } from '@/components/po/ApprovalModal'
import { CreatePOModal } from '@/components/po/CreatePOModal'
import { EditPOModal } from '@/components/po/EditPOModal'
import { MarkOrderedModal } from '@/components/po/MarkOrderedModal'
import { OrderKentuckyModal } from '@/components/po/OrderKentuckyModal'
import { POFilters } from '@/components/po/POFilters'
import { POTable } from '@/components/po/POTable'
import { BackorderTab } from '@/components/po/BackorderTab'
import { TrackingModal } from '@/components/po/TrackingModal'
import { useAllPOs, usePOBudget, usePOCounts, usePOs } from '@/hooks/usePOs'
import { useUIStore } from '@/store/uiStore'
import { fmtIsoDate } from '@/lib/dates'
import { MetricCard } from '@/components/shared/MetricCard'

// Map ?action=… query-string values to the activeModal enum in uiStore.
// Used by deep links in transactional emails so a click on
//   https://inv2.dapperhorse.com/purchase-orders?po=#3530&action=approve
// opens the right modal on the right PO instead of dumping the user at
// the bare list.
//
// All actions route to EditPOModal, the unified PO surface: it adapts by PO
// status — AWAITING_APPROVAL lands on the Review step with an inline
// Approve/Reject panel, APPROVED shows a Mark Ordered action, and every status
// is editable via the step rail + Save changes. So a recipient clicking
// "approve" gets edit + approve/reject in one modal rather than being split
// across a separate ApprovalModal. The action key is preserved (rather than
// collapsed to one value) so we can re-route an individual action later
// without touching the email templates.
const EMAIL_ACTION_TO_MODAL: Record<string, 'edit' | 'approval' | 'markOrdered'> = {
  view: 'edit',
  approve: 'edit',
  reject: 'edit',
  ordered: 'edit',
}

const ORDERED_STATUSES = new Set(['ORDERED', 'PARTIAL_ORDERED'])

function formatCurrency(val: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val)
}

function downloadCsv(filename: string, rows: Record<string, unknown>[]) {
  if (!rows.length) return
  const cols = Object.keys(rows[0])
  const escape = (v: unknown) => {
    const s = v == null ? '' : String(v)
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s
  }
  const csv = [cols.join(','), ...rows.map((r) => cols.map((c) => escape(r[c])).join(','))].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function KpiStrip() {
  const { data: allPOs = [] } = useAllPOs()
  const { data: budget } = usePOBudget()

  const orderedCost = allPOs
    .filter((p) => ORDERED_STATUSES.has(p.status))
    .reduce((s, p) => s + (p.total_cost_base ?? 0), 0)

  return (
    <div className="flex border-b border-[var(--line-warm)] bg-[var(--surface-card)]">
      <MetricCard
        label="Retail value (ordered)"
        value="—"
        delta="Backend aggregation pending"
      />
      <MetricCard
        label="Total cost (ordered)"
        value={formatCurrency(orderedCost)}
        delta={`${allPOs.filter((p) => ORDERED_STATUSES.has(p.status)).length} POs in transit`}
      />
      <MetricCard
        label="This month"
        value={budget ? formatCurrency(budget.spent_dollars) : '—'}
        delta={
          budget
            ? `of ${formatCurrency(budget.budget_dollars)} budget`
            : undefined
        }
      />
    </div>
  )
}

function TableSkeleton() {
  return (
    <div className="p-4 space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  )
}

export default function PurchaseOrders() {
  const { filters, clearFilters, activeView } = useUIStore()
  const setActiveModal = useUIStore((s) => s.setActiveModal)
  const { data: pos = [], isLoading, isError, refetch } = usePOs(filters)
  const counts = usePOCounts()
  const openCount =
    counts.DRAFT + counts.AWAITING_APPROVAL + counts.APPROVED
  const inTransitCount = counts.ORDERED
  const archivedCount = counts.ARCHIVED

  // Deep-link handler — open the requested modal when arriving via an email link.
  // Strips the query params after handling so a hard refresh doesn't re-open
  // the modal (and keeps the URL bar tidy).
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const poId = searchParams.get('po')
    const action = searchParams.get('action') ?? 'view'
    if (!poId) return
    const modal = EMAIL_ACTION_TO_MODAL[action]
    if (!modal) return
    setActiveModal(modal, poId)
    // Remove the params so the URL settles to a clean /purchase-orders
    setSearchParams({}, { replace: true })
    // Run once on mount; modal state lives in uiStore from here on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="brand-theme flex flex-col h-screen bg-[var(--surface-page)] text-[var(--text-primary)]">
      <TopTabs />
      {/* Page header */}
      <div className="flex items-center gap-3 border-b border-[var(--line-warm)] px-5 py-3.5 bg-[var(--surface-card)]">
        <h1 className="text-lg font-semibold tracking-tight text-[var(--text-primary)]">
          Purchase orders
        </h1>
        <div className="text-xs text-[var(--mute)]">
          {openCount} open · {inTransitCount} in transit · {archivedCount} archived
        </div>
        <div className="flex-1" />
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          onClick={() => {
            if (!pos.length) { return }
            const date = fmtIsoDate(new Date().toISOString())
            downloadCsv(`purchase-orders-${date}.csv`, pos.map((p) => ({
              'PO #': p.po_number || p.po_id,
              'Vendor': p.vendor,
              'Supplier': p.supplier,
              'Status': p.status,
              'Items': p.items_count,
              'Generated': fmtIsoDate(p.created_date),
              'Ordered': fmtIsoDate(p.ordered_date),
              'Expected Delivery': fmtIsoDate(p.expected_delivery_date),
              'Total Cost': p.total_cost_base,
              'Ordered Units': p.ordered_units,
              'Received Units': p.received_units,
              'Location': p.location_to_receive,
            })))
          }}
        >
          <Download className="mr-1 h-3 w-3" />
          Export
        </Button>
        <Button
          size="sm"
          className="h-7 text-xs"
          onClick={() => setActiveModal('create')}
        >
          + New PO
        </Button>
      </div>

      {/* KPI tiles — PO list view only */}
      {activeView === 'pos' && <KpiStrip />}

      {/* Filters (tab strip always visible; toolbar gates on view internally) */}
      <POFilters />

      {/* Backorder / OOS view */}
      {activeView === 'backorder' && (
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          <BackorderTab />
        </div>
      )}

      {/* Table area — PO list view */}
      {activeView === 'pos' && (
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        {isLoading && <TableSkeleton />}

        {isError && !isLoading && (
          <div className="flex justify-center py-12">
            <Card className="w-80 rounded-none shadow-none border-[var(--line-warm)] bg-[var(--surface-card)]">
              <CardContent className="flex flex-col items-center gap-3 pt-6 pb-5">
                <AlertCircle className="h-8 w-8 text-[var(--status-rejected-fg)]" />
                <p className="text-sm text-[var(--ink-2)] text-center">
                  Failed to load purchase orders
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs"
                  onClick={() => refetch()}
                >
                  <RefreshCw className="mr-1.5 h-3 w-3" />
                  Retry
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {!isLoading && !isError && (
          <POTable data={pos} onClearFilters={clearFilters} />
        )}
      </div>
      )}

      <CreatePOModal />
      <EditPOModal />
      <ApprovalModal />
      <MarkOrderedModal />
      <OrderKentuckyModal />
      <TrackingModal />
    </div>
  )
}
